import { IManifoldLog } from '../../models/manifold';

/** Heartbeat age (minutes) after which a crawler is flagged as delayed (yellow / WARN). */
export const CRAWLER_WARN_MINUTES = 5;
/** Heartbeat age (minutes) after which a crawler is considered offline (red / ERR). */
export const CRAWLER_OFFLINE_MINUTES = 15;

/**
 * Overall crawler health. The single source of truth for both the Monitoring nav badge and the
 * /manifold global-status banner, so the two can never disagree.
 */
export type CrawlerHealth = 'paused' | 'red' | 'yellow' | 'indigo' | 'green' | 'checking';

/** The authoritative liveness timestamp: the fast /heartbeat if we have it, else the /stats log time. */
export function effectiveHeartbeat(s: Pick<IManifoldLog, 'lastHeartbeatAt' | 'timestamp'>): string {
  return s.lastHeartbeatAt || s.timestamp;
}

/** Minutes since the authoritative heartbeat. Thresholds above are tuned to the fast (15s) heartbeat. */
export function heartbeatAgeMinutes(s: Pick<IManifoldLog, 'lastHeartbeatAt' | 'timestamp'>): number {
  return (Date.now() - new Date(effectiveHeartbeat(s)).getTime()) / (1000 * 60);
}

export function crawlerCommit(s: Pick<IManifoldLog, 'scraperLogSummary'>): string | null {
  if (!s.scraperLogSummary) return 'unset';
  return typeof s.scraperLogSummary === 'object' ? s.scraperLogSummary.gitCommit : s.scraperLogSummary;
}

export function isCrawlerOutdated(s: Pick<IManifoldLog, 'scraperLogSummary'>, latestCommit: string | null | undefined): boolean {
  if (!latestCommit) return false;
  if (!s.scraperLogSummary) return true;
  const commit = crawlerCommit(s);
  if (!commit) return false;
  return commit !== latestCommit;
}

/** Worst state across the (already localhost-filtered) crawlers; a paused queue outranks everything. */
export function computeCrawlerHealth(statuses: IManifoldLog[], latestCommit: string | null | undefined, paused: boolean): CrawlerHealth {
  if (paused) return 'paused';
  if (!statuses.length) return 'checking';
  if (statuses.some((s) => heartbeatAgeMinutes(s) > CRAWLER_OFFLINE_MINUTES)) return 'red';
  if (statuses.some((s) => heartbeatAgeMinutes(s) > CRAWLER_WARN_MINUTES)) return 'yellow';
  if (statuses.some((s) => isCrawlerOutdated(s, latestCommit))) return 'indigo';
  return 'green';
}
